'use client';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { FileText, Download, Eye, Shield, Search, Lock, History, X, RefreshCw, FileSignature, Receipt, Banknote, Calendar, Tag, Archive, Upload, CheckCircle, AlertTriangle, Clock, ShieldCheck, ShieldX, Activity, Users, FileWarning, ScanLine, ClipboardList, Globe, Building2, Fingerprint, Layers, BookOpen, Check } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

type DocCategory = 'KYC' | 'NDA' | 'Investment Agreement' | 'Settlement' | 'Distribution' | 'Campaign Disclosure' | 'Compliance Evidence' | 'AML Report' | 'FATCA/CRS';
type DocStatus = 'active' | 'superseded' | 'pending' | 'expired' | 'quarantined' | 'deleted';
type ScanStatus = 'pending' | 'clean' | 'infected' | 'error';
type NDAStatus = 'not_required' | 'pending' | 'signed' | 'expired' | 'revoked';
type AuditAction = 'upload' | 'view' | 'download' | 'delete' | 'version_added' | 'nda_signed' | 'nda_revoked' | 'scan_completed' | 'retention_applied' | 'access_denied' | 'role_permission_changed' | 'expiry_updated';

interface DocVersion {
  id: string;
  version: string;
  file_size_bytes: number;
  uploaded_by: string;
  uploaded_by_role: string;
  change_note: string;
  scan_status: ScanStatus;
  is_current: boolean;
  created_at: string;
}

interface VaultDocument {
  id: string;
  doc_ref: string;
  name: string;
  category: DocCategory;
  campaign?: string;
  doc_status: DocStatus;
  file_size_bytes: number;
  current_version: string;
  access_level: string;
  signed_by?: string;
  expiry_date?: string;
  allowed_roles: string[];
  uploaded_by: string;
  uploaded_by_role: string;
  requires_nda: boolean;
  nda_status: NDAStatus;
  nda_signed_at?: string;
  nda_signed_by?: string;
  scan_status: ScanStatus;
  scan_completed_at?: string;
  scan_engine?: string;
  scan_threat_name?: string;
  retention_days: number;
  retention_label: string;
  retention_expires_at?: string;
  auto_delete_on_expiry: boolean;
  tags: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
  versions?: DocVersion[];
}

interface AuditEntry {
  id: string;
  doc_ref: string;
  doc_name: string;
  action: AuditAction;
  actor_name: string;
  actor_role: string;
  actor_ip?: string;
  details: Record<string, unknown>;
  outcome: string;
  outcome_reason?: string;
  created_at: string;
}

interface RolePermission {
  role: string;
  category: DocCategory;
  can_upload: boolean;
  can_view: boolean;
  can_download: boolean;
  can_delete: boolean;
  requires_nda_to_view: boolean;
  max_retention_days: number;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const CURRENT_ROLE = 'compliance'; // Simulated active role

const categoryConfig: Record<DocCategory, { icon: React.ElementType; color: string; bg: string; label: string }> = {
  'KYC':                  { icon: Shield,       color: 'text-primary',  bg: 'bg-primary/10 border-primary/20',  label: 'KYC Documents' },
  'NDA':                  { icon: Lock,         color: 'text-warning',  bg: 'bg-warning/10 border-warning/20',  label: 'Campaign NDAs' },
  'Investment Agreement': { icon: FileSignature, color: 'text-accent',   bg: 'bg-accent/10 border-accent/20',    label: 'Investment Agreements' },
  'Settlement':           { icon: Receipt,      color: 'text-info',     bg: 'bg-info/10 border-info/20',        label: 'Settlement Confirmations' },
  'Distribution':         { icon: Banknote,     color: 'text-positive', bg: 'bg-positive/10 border-positive/20', label: 'Distribution Receipts' },
  'Campaign Disclosure':  { icon: Globe,        color: 'text-accent',   bg: 'bg-accent/10 border-accent/20',    label: 'Campaign Disclosures' },
  'Compliance Evidence':  { icon: ShieldCheck,  color: 'text-primary',  bg: 'bg-primary/10 border-primary/20',  label: 'Compliance Evidence' },
  'AML Report':           { icon: FileWarning,  color: 'text-negative', bg: 'bg-negative/10 border-negative/20', label: 'AML Reports' },
  'FATCA/CRS':            { icon: Building2,    color: 'text-info',     bg: 'bg-info/10 border-info/20',        label: 'FATCA / CRS' },
};

const statusConfig: Record<DocStatus, { variant: string; label: string }> = {
  active:      { variant: 'confirmed', label: 'Active' },
  superseded:  { variant: 'closed',    label: 'Superseded' },
  pending:     { variant: 'pending',   label: 'Pending' },
  expired:     { variant: 'failed',    label: 'Expired' },
  quarantined: { variant: 'critical',  label: 'Quarantined' },
  deleted:     { variant: 'closed',    label: 'Deleted' },
};

const scanConfig: Record<ScanStatus, { icon: React.ElementType; color: string; label: string }> = {
  pending:  { icon: ScanLine,   color: 'text-warning',  label: 'Scan Pending' },
  clean:    { icon: ShieldCheck, color: 'text-positive', label: 'Clean' },
  infected: { icon: ShieldX,    color: 'text-negative', label: 'Threat Detected' },
  error:    { icon: AlertTriangle, color: 'text-warning', label: 'Scan Error' },
};

const ndaConfig: Record<NDAStatus, { color: string; label: string }> = {
  not_required: { color: 'text-muted-foreground', label: 'Not Required' },
  pending:      { color: 'text-warning',          label: 'NDA Required' },
  signed:       { color: 'text-positive',         label: 'NDA Signed' },
  expired:      { color: 'text-negative',         label: 'NDA Expired' },
  revoked:      { color: 'text-negative',         label: 'NDA Revoked' },
};

const auditActionConfig: Record<AuditAction, { color: string; label: string }> = {
  upload:                  { color: 'text-positive', label: 'Upload' },
  view:                    { color: 'text-info',     label: 'View' },
  download:                { color: 'text-accent',   label: 'Download' },
  delete:                  { color: 'text-negative', label: 'Delete' },
  version_added:           { color: 'text-primary',  label: 'Version Added' },
  nda_signed:              { color: 'text-positive', label: 'NDA Signed' },
  nda_revoked:             { color: 'text-negative', label: 'NDA Revoked' },
  scan_completed:          { color: 'text-info',     label: 'Scan Completed' },
  retention_applied:       { color: 'text-warning',  label: 'Retention Applied' },
  access_denied:           { color: 'text-negative', label: 'Access Denied' },
  role_permission_changed: { color: 'text-warning',  label: 'Permission Changed' },
  expiry_updated:          { color: 'text-warning',  label: 'Expiry Updated' },
};

const allCategories: DocCategory[] = ['KYC', 'NDA', 'Investment Agreement', 'Settlement', 'Distribution', 'Campaign Disclosure', 'Compliance Evidence', 'AML Report', 'FATCA/CRS'];

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Upload Modal ─────────────────────────────────────────────────────────────

function UploadModal({
  onClose,
  onUpload,
  permissions,
}: {
  onClose: () => void;
  onUpload: (data: Partial<VaultDocument>) => void;
  permissions: RolePermission[];
}) {
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [category, setCategory] = useState<DocCategory>('KYC');
  const [campaign, setCampaign] = useState('');
  const [notes, setNotes] = useState('');
  const [requiresNda, setRequiresNda] = useState(false);
  const [retentionDays, setRetentionDays] = useState(2555);
  const [scanning, setScanning] = useState(false);
  const [scanDone, setScanDone] = useState(false);

  const uploadableCategories = permissions.filter(p => p.can_upload).map(p => p.category);

  const handleSimulateUpload = () => {
    if (!fileName) return;
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
      setScanDone(true);
    }, 1800);
  };

  const handleConfirm = () => {
    onUpload({
      name: fileName || 'Uploaded Document.pdf',
      category,
      campaign: campaign || undefined,
      doc_status: 'active',
      file_size_bytes: Math.floor(Math.random() * 3000000 + 500000),
      current_version: 'v1.0',
      access_level: category === 'Compliance Evidence' || category === 'AML Report' ? 'Restricted' : 'Confidential',
      requires_nda: requiresNda,
      nda_status: requiresNda ? 'pending' : 'not_required',
      scan_status: 'clean',
      retention_days: retentionDays,
      retention_label: retentionDays >= 3650 ? '10 years (regulatory)' : retentionDays >= 2555 ? '7 years (regulatory)' : '5 years',
      uploaded_by: 'Current User',
      uploaded_by_role: CURRENT_ROLE,
      allowed_roles: ['compliance', 'admin', 'auditor'],
      tags: [],
      notes,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-card-elevated rounded-2xl w-full max-w-lg border border-border shadow-2xl z-10 overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-surface-2">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Upload size={15} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Upload Document</h3>
              <p className="text-xs text-muted-foreground">Role: <span className="text-primary font-medium capitalize">{CURRENT_ROLE}</span></p>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5"><X size={15} /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => { e.preventDefault(); setDragging(false); setFileName('document_' + Date.now() + '.pdf'); }}
            onClick={() => setFileName('document_' + Date.now() + '.pdf')}
            className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-surface-3'}`}
          >
            {fileName ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={18} className="text-primary" />
                <span className="text-sm font-medium text-foreground truncate max-w-xs">{fileName}</span>
                <button onClick={(e) => { e.stopPropagation(); setFileName(''); setScanDone(false); }} className="text-muted-foreground hover:text-negative">
                  <X size={13} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={22} className="mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground">Drop file or click to browse</p>
                <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, XLSX — max 50 MB</p>
              </>
            )}
          </div>

          {/* Virus scan status */}
          {fileName && (
            <div className={`rounded-lg border p-3 flex items-center gap-3 ${scanDone ? 'bg-positive/5 border-positive/20' : scanning ? 'bg-warning/5 border-warning/20' : 'bg-surface-3 border-border'}`}>
              {scanning ? (
                <>
                  <ScanLine size={15} className="text-warning animate-pulse" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-foreground">Virus scan in progress…</p>
                    <p className="text-xs text-muted-foreground">ClamAV engine scanning file</p>
                  </div>
                  <RefreshCw size={13} className="text-warning animate-spin" />
                </>
              ) : scanDone ? (
                <>
                  <ShieldCheck size={15} className="text-positive" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-positive">File is clean</p>
                    <p className="text-xs text-muted-foreground">No threats detected · ClamAV</p>
                  </div>
                  <CheckCircle size={13} className="text-positive" />
                </>
              ) : (
                <>
                  <ScanLine size={15} className="text-muted-foreground" />
                  <div className="flex-1">
                    <p className="text-xs font-medium text-foreground">Virus scan required before upload</p>
                    <p className="text-xs text-muted-foreground">Click "Run Scan" to proceed</p>
                  </div>
                  <button onClick={handleSimulateUpload} className="btn-secondary text-xs px-3 py-1.5">Run Scan</button>
                </>
              )}
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Document Category <span className="text-negative">*</span></label>
            <select
              value={category}
              onChange={e => setCategory(e.target.value as DocCategory)}
              className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {uploadableCategories.length > 0
                ? uploadableCategories.map(c => <option key={c} value={c}>{c}</option>)
                : allCategories.map(c => <option key={c} value={c}>{c}</option>)
              }
            </select>
            {uploadableCategories.length === 0 && (
              <p className="text-xs text-negative mt-1 flex items-center gap-1"><AlertTriangle size={11} /> Your role does not have upload permission for any category.</p>
            )}
          </div>

          {/* Campaign (for NDA / Disclosure) */}
          {(category === 'NDA' || category === 'Campaign Disclosure') && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Campaign</label>
              <input
                value={campaign}
                onChange={e => setCampaign(e.target.value)}
                placeholder="e.g. Meridian Logistics Hub — Series B"
                className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          {/* NDA Gating */}
          <div className="rounded-xl border border-border bg-surface-2 p-3.5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock size={13} className="text-warning" />
                <span className="text-xs font-semibold text-foreground">NDA Gating</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={requiresNda} onChange={e => setRequiresNda(e.target.checked)} className="sr-only peer" />
                <div className="w-9 h-5 bg-surface-4 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
            {requiresNda && (
              <p className="text-xs text-muted-foreground">Investors must sign an NDA before accessing this document. NDA signature will be logged in the audit trail.</p>
            )}
          </div>

          {/* Retention Policy */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Retention Policy</label>
            <select
              value={retentionDays}
              onChange={e => setRetentionDays(Number(e.target.value))}
              className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value={1825}>5 years (NDA / Disclosure)</option>
              <option value={2555}>7 years (Regulatory standard)</option>
              <option value={3650}>10 years (Legal / AML)</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional upload notes or change description…"
              className="w-full px-3 py-2 rounded-lg bg-surface-3 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2 bg-surface-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={!fileName || !scanDone}
            className="btn-primary text-sm gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload size={13} /> Upload Document
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── NDA Gate Modal ───────────────────────────────────────────────────────────

function NDAGateModal({ doc, onSign, onClose }: { doc: VaultDocument; onSign: (docId: string) => void; onClose: () => void }) {
  const [agreed, setAgreed] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-card-elevated rounded-2xl w-full max-w-md border border-border shadow-2xl z-10 overflow-hidden">
        <div className="px-5 py-4 border-b border-border bg-warning/5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-warning/10 flex items-center justify-center">
            <Lock size={16} className="text-warning" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Non-Disclosure Agreement Required</h3>
            <p className="text-xs text-muted-foreground">{doc.campaign || doc.name}</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-lg bg-surface-3 border border-border p-4 text-xs text-muted-foreground leading-relaxed space-y-2 max-h-40 overflow-y-auto">
            <p className="font-semibold text-foreground">Confidentiality Agreement</p>
            <p>By signing this NDA, you agree to keep all information contained in the document strictly confidential. You shall not disclose, reproduce, or distribute any information to third parties without prior written consent from CrowdVault™ and the issuer.</p>
            <p>This agreement is binding for 5 years from the date of signing. Breach of this agreement may result in legal action and immediate suspension of platform access.</p>
            <p>Your signature will be recorded with timestamp, IP address, and user agent for audit purposes.</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 accent-primary" />
            <span className="text-xs text-foreground">I have read and agree to the terms of this Non-Disclosure Agreement. I understand this is a legally binding document.</span>
          </label>
        </div>
        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2 bg-surface-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button
            onClick={() => { if (agreed) { onSign(doc.id); onClose(); } }}
            disabled={!agreed}
            className="btn-primary text-sm gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Fingerprint size={13} /> Sign NDA & Access Document
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Version History Drawer ───────────────────────────────────────────────────

function VersionHistoryDrawer({ doc, onClose }: { doc: VaultDocument; onClose: () => void }) {
  const versions = doc.versions || [];
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-card-elevated rounded-2xl w-full max-w-lg border border-border shadow-2xl z-10">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground text-sm">Version History</h3>
            <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-xs">{doc.name}</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1.5"><X size={15} /></button>
        </div>
        <div className="p-5 space-y-3 max-h-96 overflow-y-auto">
          {versions.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">No version history available</p>
          ) : versions.map((v, i) => {
            const ScanIcon = scanConfig[v.scan_status].icon;
            return (
              <div key={`ver-${i}`} className={`rounded-xl border p-3.5 ${v.is_current ? 'bg-primary/5 border-primary/25' : 'bg-surface-3 border-border'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`font-mono-data text-sm font-bold ${v.is_current ? 'text-primary' : 'text-foreground'}`}>{v.version}</span>
                    {v.is_current && <span className="text-xs bg-primary/20 text-primary px-1.5 py-0.5 rounded-full font-medium">Current</span>}
                    <ScanIcon size={12} className={scanConfig[v.scan_status].color} title={scanConfig[v.scan_status].label} />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button className="btn-ghost p-1 text-xs gap-1"><Eye size={12} /></button>
                    <button className="btn-ghost p-1 text-xs gap-1"><Download size={12} /></button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed mb-2">{v.change_note}</p>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Calendar size={10} /> {formatDate(v.created_at)}</span>
                  <span className="flex items-center gap-1"><Tag size={10} /> {formatBytes(v.file_size_bytes)}</span>
                  <span className="truncate capitalize">{v.uploaded_by_role}: {v.uploaded_by}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Audit Trail Panel ────────────────────────────────────────────────────────

function AuditTrailPanel({ entries, loading }: { entries: AuditEntry[]; loading: boolean }) {
  const [filter, setFilter] = useState<AuditAction | 'all'>('all');
  const filtered = filter === 'all' ? entries : entries.filter(e => e.action === filter);

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-surface-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <ClipboardList size={15} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Document Audit Trail</h3>
            <p className="text-xs text-muted-foreground">{entries.length} events recorded</p>
          </div>
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value as AuditAction | 'all')}
          className="text-xs px-2.5 py-1.5 rounded-lg bg-surface-3 border border-border text-foreground focus:outline-none"
        >
          <option value="all">All Actions</option>
          {Object.entries(auditActionConfig).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="py-10 text-center">
          <RefreshCw size={20} className="animate-spin text-muted-foreground mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">Loading audit trail…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center">
          <Activity size={24} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No audit events found</p>
        </div>
      ) : (
        <div className="divide-y divide-border/50 max-h-96 overflow-y-auto">
          {filtered.map((entry) => {
            const actionCfg = auditActionConfig[entry.action] || { color: 'text-muted-foreground', label: entry.action };
            return (
              <div key={entry.id} className="flex items-start gap-3 px-5 py-3 hover:bg-surface-3/50 transition-colors">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${entry.outcome === 'denied' ? 'bg-negative' : entry.outcome === 'success' ? 'bg-positive' : 'bg-warning'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold ${actionCfg.color}`}>{actionCfg.label}</span>
                    <span className="text-xs text-muted-foreground truncate max-w-[200px]">{entry.doc_name}</span>
                    {entry.outcome === 'denied' && (
                      <span className="text-xs bg-negative/10 text-negative px-1.5 py-0.5 rounded-full">Denied</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    <span className="capitalize">{entry.actor_role}: {entry.actor_name}</span>
                    {entry.actor_ip && <span className="font-mono-data hidden sm:block">{entry.actor_ip}</span>}
                  </div>
                </div>
                <span className="text-xs text-muted-foreground flex-shrink-0 font-mono-data">{formatDate(entry.created_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Role Permissions Panel ───────────────────────────────────────────────────

function RolePermissionsPanel({ permissions }: { permissions: RolePermission[] }) {
  const roles = ['investor', 'compliance', 'admin', 'auditor', 'trustee', 'issuer'];
  const [selectedRole, setSelectedRole] = useState('compliance');
  const rolePerms = permissions.filter(p => p.role === selectedRole);

  return (
    <div className="glass-card rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between bg-surface-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center">
            <Users size={15} className="text-warning" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm">Role-Scoped Permissions</h3>
            <p className="text-xs text-muted-foreground">Document access matrix by role</p>
          </div>
        </div>
        <div className="flex gap-1 bg-surface-3 rounded-lg p-1">
          {roles.map(r => (
            <button
              key={r}
              onClick={() => setSelectedRole(r)}
              className={`text-xs px-2.5 py-1 rounded-md transition-all capitalize ${selectedRole === r ? 'bg-surface-4 text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface-3">
            <tr>
              {['Category', 'View', 'Upload', 'Download', 'Delete', 'NDA Required', 'Retention'].map(h => (
                <th key={h} className="table-th text-xs">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rolePerms.map((perm) => {
              const cfg = categoryConfig[perm.category];
              const CatIcon = cfg.icon;
              return (
                <tr key={perm.category} className="data-table-row">
                  <td className="table-td">
                    <div className="flex items-center gap-2">
                      <CatIcon size={12} className={cfg.color} />
                      <span className="text-xs text-foreground">{perm.category}</span>
                    </div>
                  </td>
                  {[perm.can_view, perm.can_upload, perm.can_download, perm.can_delete].map((val, i) => (
                    <td key={i} className="table-td text-center">
                      {val
                        ? <Check size={13} className="text-positive mx-auto" />
                        : <X size={13} className="text-negative/50 mx-auto" />
                      }
                    </td>
                  ))}
                  <td className="table-td text-center">
                    {perm.requires_nda_to_view
                      ? <Lock size={12} className="text-warning mx-auto" />
                      : <span className="text-xs text-muted-foreground">—</span>
                    }
                  </td>
                  <td className="table-td">
                    <span className="text-xs text-muted-foreground font-mono-data">{Math.floor(perm.max_retention_days / 365)}y</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Document Row ─────────────────────────────────────────────────────────────

function DocumentRow({
  doc,
  onViewHistory,
  onNDAGate,
  currentRole,
  permissions,
}: {
  doc: VaultDocument;
  onViewHistory: (doc: VaultDocument) => void;
  onNDAGate: (doc: VaultDocument) => void;
  currentRole: string;
  permissions: RolePermission[];
}) {
  const catCfg = categoryConfig[doc.category];
  const statCfg = statusConfig[doc.doc_status];
  const scanCfg = scanConfig[doc.scan_status];
  const ndaCfg = ndaConfig[doc.nda_status];
  const CatIcon = catCfg.icon;
  const ScanIcon = scanCfg.icon;

  const router = useRouter();
  const perm = permissions.find(p => p.role === currentRole && p.category === doc.category);
  const canView = perm?.can_view ?? false;
  const canDownload = perm?.can_download ?? false;
  const needsNDA = doc.requires_nda && doc.nda_status !== 'signed';

  const handleView = () => {
    if (needsNDA) { onNDAGate(doc); return; }
  };

  return (
    <tr className="data-table-row group">
      <td className="table-td">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border ${catCfg.bg}`}>
            <CatIcon size={14} className={catCfg.color} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <button onClick={() => router.push(`/document-vault/${doc.id}`)} className="font-medium text-foreground text-sm truncate max-w-[200px] hover:text-primary transition-colors text-left">{doc.name}</button>
              {doc.requires_nda && (
                <Lock size={11} className={ndaCfg.color} title={ndaCfg.label} />
              )}
            </div>
            {doc.campaign && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{doc.campaign}</p>}
          </div>
        </div>
      </td>
      <td className="table-td">
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${catCfg.bg} ${catCfg.color}`}>{doc.category}</span>
      </td>
      <td className="table-td">
        <div className="flex items-center gap-1.5">
          <ScanIcon size={12} className={scanCfg.color} />
          <span className={`text-xs ${scanCfg.color}`}>{scanCfg.label}</span>
        </div>
      </td>
      <td className="table-td">
        <StatusBadge variant={statCfg.variant as any} label={statCfg.label} dot />
      </td>
      <td className="table-td">
        <div className="flex items-center gap-1">
          <span className="text-xs font-mono-data text-muted-foreground">{doc.current_version}</span>
          {doc.versions && doc.versions.length > 1 && (
            <button onClick={() => onViewHistory(doc)} className="flex items-center gap-0.5 text-xs text-accent hover:text-accent/80 ml-1">
              <History size={11} />
              <span>{doc.versions.length}</span>
            </button>
          )}
        </div>
      </td>
      <td className="table-td">
        <div className="text-xs">
          <p className="text-muted-foreground font-mono-data">{doc.retention_label}</p>
          {doc.retention_expires_at && (
            <p className="text-muted-foreground/70">Exp: {formatDate(doc.retention_expires_at)}</p>
          )}
        </div>
      </td>
      <td className="table-td">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {canView && (
            <button onClick={handleView} className="btn-ghost p-1.5" title={needsNDA ? 'Sign NDA to view' : 'View'}>
              {needsNDA ? <Lock size={13} className="text-warning" /> : <Eye size={13} />}
            </button>
          )}
          {canDownload && !needsNDA && (
            <button className="btn-ghost p-1.5" title="Download"><Download size={13} /></button>
          )}
          {doc.versions && doc.versions.length > 1 && (
            <button className="btn-ghost p-1.5" title="Version history" onClick={() => onViewHistory(doc)}>
              <History size={13} />
            </button>
          )}
          {!canView && (
            <span className="text-xs text-negative/70 flex items-center gap-1"><Lock size={11} /> No access</span>
          )}
        </div>
      </td>
    </tr>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DocumentVault() {
  const supabase = createClient();
  const router = useRouter();

  const [documents, setDocuments] = useState<VaultDocument[]>([]);
  const [auditEntries, setAuditEntries] = useState<AuditEntry[]>([]);
  const [permissions, setPermissions] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<DocCategory | 'All'>('All');
  const [activeView, setActiveView] = useState<'table' | 'grouped' | 'audit' | 'permissions'>('grouped');
  const [historyDoc, setHistoryDoc] = useState<VaultDocument | null>(null);
  const [ndaDoc, setNdaDoc] = useState<VaultDocument | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [scanFilter, setScanFilter] = useState<ScanStatus | 'all'>('all');

  // ─── Fetch Data ──────────────────────────────────────────────────────────────

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const { data: docs } = await supabase
        .from('vault_documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (docs && docs.length > 0) {
        const { data: versions } = await supabase
          .from('vault_document_versions')
          .select('*')
          .order('created_at', { ascending: false });

        const docsWithVersions = docs.map((d: VaultDocument) => ({
          ...d,
          versions: versions?.filter((v: DocVersion) => (v as any).document_id === d.id) || [],
        }));
        setDocuments(docsWithVersions);
      } else {
        setDocuments(getFallbackDocuments());
      }
    } catch {
      setDocuments(getFallbackDocuments());
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  const fetchAuditTrail = useCallback(async () => {
    setAuditLoading(true);
    try {
      const { data } = await supabase
        .from('vault_audit_trail')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (data && data.length > 0) setAuditEntries(data);
      else setAuditEntries(getFallbackAuditEntries());
    } catch {
      setAuditEntries(getFallbackAuditEntries());
    } finally {
      setAuditLoading(false);
    }
  }, [supabase]);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await supabase.from('vault_role_permissions').select('*');
      if (data && data.length > 0) setPermissions(data);
      else setPermissions(getFallbackPermissions());
    } catch {
      setPermissions(getFallbackPermissions());
    }
  }, [supabase]);

  useEffect(() => {
    fetchDocuments();
    fetchAuditTrail();
    fetchPermissions();
  }, [fetchDocuments, fetchAuditTrail, fetchPermissions]);

  // ─── Realtime ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const channel = supabase
      .channel('vault-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vault_documents' }, () => fetchDocuments())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'vault_audit_trail' }, (payload) => {
        setAuditEntries(prev => [payload.new as AuditEntry, ...prev].slice(0, 100));
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [supabase, fetchDocuments]);

  // ─── Handlers ────────────────────────────────────────────────────────────────

  const handleUpload = async (data: Partial<VaultDocument>) => {
    const docRef = `VD-${data.category?.substring(0, 3).toUpperCase()}-${Date.now()}`;
    const newDoc: VaultDocument = {
      id: crypto.randomUUID(),
      doc_ref: docRef,
      name: data.name || 'Uploaded Document',
      category: data.category || 'KYC',
      campaign: data.campaign,
      doc_status: 'active',
      file_size_bytes: data.file_size_bytes || 0,
      current_version: 'v1.0',
      access_level: data.access_level || 'Confidential',
      allowed_roles: data.allowed_roles || ['compliance', 'admin'],
      uploaded_by: data.uploaded_by || 'Current User',
      uploaded_by_role: data.uploaded_by_role || CURRENT_ROLE,
      requires_nda: data.requires_nda || false,
      nda_status: data.nda_status || 'not_required',
      scan_status: 'clean',
      scan_completed_at: new Date().toISOString(),
      retention_days: data.retention_days || 2555,
      retention_label: data.retention_label || '7 years (regulatory)',
      retention_expires_at: new Date(Date.now() + (data.retention_days || 2555) * 86400000).toISOString(),
      auto_delete_on_expiry: false,
      tags: data.tags || [],
      notes: data.notes,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      versions: [{ id: crypto.randomUUID(), version: 'v1.0', file_size_bytes: data.file_size_bytes || 0, uploaded_by: data.uploaded_by || 'Current User', uploaded_by_role: CURRENT_ROLE, change_note: data.notes || 'Initial upload', scan_status: 'clean', is_current: true, created_at: new Date().toISOString() }],
    };

    try {
      await supabase.from('vault_documents').insert({
        doc_ref: docRef, name: newDoc.name, category: newDoc.category,
        campaign: newDoc.campaign, doc_status: 'active',
        file_size_bytes: newDoc.file_size_bytes, current_version: 'v1.0',
        access_level: newDoc.access_level, allowed_roles: newDoc.allowed_roles,
        uploaded_by: newDoc.uploaded_by, uploaded_by_role: newDoc.uploaded_by_role,
        requires_nda: newDoc.requires_nda, nda_status: newDoc.nda_status,
        scan_status: 'clean', scan_completed_at: newDoc.scan_completed_at,
        retention_days: newDoc.retention_days, retention_label: newDoc.retention_label,
        retention_expires_at: newDoc.retention_expires_at,
        auto_delete_on_expiry: false, tags: newDoc.tags, notes: newDoc.notes,
      });
      await supabase.from('vault_audit_trail').insert({
        doc_ref: docRef, doc_name: newDoc.name, action: 'upload',
        actor_name: 'Current User', actor_role: CURRENT_ROLE,
        details: { version: 'v1.0', size_bytes: newDoc.file_size_bytes, scan: 'clean' },
        outcome: 'success',
      });
    } catch {
      // Optimistic update
    }

    setDocuments(prev => [newDoc, ...prev]);
    const auditEntry: AuditEntry = {
      id: crypto.randomUUID(), doc_ref: docRef, doc_name: newDoc.name,
      action: 'upload', actor_name: 'Current User', actor_role: CURRENT_ROLE,
      details: { version: 'v1.0' }, outcome: 'success', created_at: new Date().toISOString(),
    };
    setAuditEntries(prev => [auditEntry, ...prev]);
    setShowUpload(false);
  };

  const handleNDASign = async (docId: string) => {
    setDocuments(prev => prev.map(d => d.id === docId ? { ...d, nda_status: 'signed' as NDAStatus, nda_signed_at: new Date().toISOString(), nda_signed_by: 'Current User' } : d));
    const doc = documents.find(d => d.id === docId);
    if (doc) {
      const auditEntry: AuditEntry = {
        id: crypto.randomUUID(), doc_ref: doc.doc_ref, doc_name: doc.name,
        action: 'nda_signed', actor_name: 'Current User', actor_role: CURRENT_ROLE,
        details: { campaign: doc.campaign }, outcome: 'success', created_at: new Date().toISOString(),
      };
      setAuditEntries(prev => [auditEntry, ...prev]);
      try {
        await supabase.from('vault_documents').update({ nda_status: 'signed', nda_signed_at: new Date().toISOString(), nda_signed_by: 'Current User' }).eq('id', docId);
        await supabase.from('vault_audit_trail').insert({ doc_ref: doc.doc_ref, doc_name: doc.name, action: 'nda_signed', actor_name: 'Current User', actor_role: CURRENT_ROLE, details: { campaign: doc.campaign }, outcome: 'success' });
      } catch { /* optimistic */ }
    }
  };

  // ─── Filtered / Grouped ───────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    return documents.filter((d) => {
      if (selectedCategory !== 'All' && d.category !== selectedCategory) return false;
      if (scanFilter !== 'all' && d.scan_status !== scanFilter) return false;
      if (search && !d.name.toLowerCase().includes(search.toLowerCase()) &&
        !(d.campaign?.toLowerCase().includes(search.toLowerCase())) &&
        !(d.doc_ref?.toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });
  }, [documents, search, selectedCategory, scanFilter]);

  const grouped = useMemo(() => {
    const map: Partial<Record<DocCategory, VaultDocument[]>> = {};
    filtered.forEach((d) => {
      if (!map[d.category]) map[d.category] = [];
      map[d.category]!.push(d);
    });
    return map;
  }, [filtered]);

  const kpiCounts = useMemo(() => ({
    total: documents.length,
    clean: documents.filter(d => d.scan_status === 'clean').length,
    quarantined: documents.filter(d => d.scan_status === 'infected').length,
    ndaPending: documents.filter(d => d.nda_status === 'pending').length,
    expiringSoon: documents.filter(d => d.expiry_date && new Date(d.expiry_date) < new Date(Date.now() + 30 * 86400000)).length,
  }), [documents]);

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Document Vault</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Role-scoped · NDA-gated · Virus-scanned · Retention-managed · Fully audited
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { fetchDocuments(); fetchAuditTrail(); }} className="btn-secondary text-xs gap-1.5">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={() => setShowUpload(true)} className="btn-primary text-xs gap-1.5">
            <Upload size={13} /> Upload Document
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: 'Total Documents', value: kpiCounts.total, icon: FileText, color: 'text-primary', bg: 'bg-primary/10 border-primary/20' },
          { label: 'Virus-Scanned Clean', value: kpiCounts.clean, icon: ShieldCheck, color: 'text-positive', bg: 'bg-positive/10 border-positive/20' },
          { label: 'Quarantined', value: kpiCounts.quarantined, icon: ShieldX, color: 'text-negative', bg: 'bg-negative/10 border-negative/20' },
          { label: 'NDA Pending', value: kpiCounts.ndaPending, icon: Lock, color: 'text-warning', bg: 'bg-warning/10 border-warning/20' },
          { label: 'Expiring ≤30d', value: kpiCounts.expiringSoon, icon: Clock, color: 'text-warning', bg: 'bg-warning/10 border-warning/20' },
        ].map(({ label, value, icon, color, bg }) => (
          <div key={label} className="glass-card rounded-xl p-3.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 border ${bg}`}>
              {React.createElement(icon, { size: 15, className: color })}
            </div>
            <p className={`font-mono-data font-bold text-xl ${color}`}>{value}</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{label}</p>
          </div>
        ))}
      </div>

      {/* Category filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setSelectedCategory('All')}
          className={`text-xs px-3 py-1.5 rounded-full border transition-all ${selectedCategory === 'All' ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}
        >
          All
        </button>
        {allCategories.map(cat => {
          const cfg = categoryConfig[cat];
          const CatIcon = cfg.icon;
          return (
            <button
              key={cat}
              onClick={() => setSelectedCategory(selectedCategory === cat ? 'All' : cat)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-all ${selectedCategory === cat ? `${cfg.bg} ${cfg.color} border-current` : 'border-border text-muted-foreground hover:border-primary/50'}`}
            >
              <CatIcon size={11} />
              {cat}
            </button>
          );
        })}
      </div>

      {/* Search + View Toggle */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents, campaigns, refs…"
            className="input-field pl-9 text-sm"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X size={13} />
            </button>
          )}
        </div>
        <select
          value={scanFilter}
          onChange={e => setScanFilter(e.target.value as ScanStatus | 'all')}
          className="text-xs px-2.5 py-2 rounded-lg bg-surface-3 border border-border text-foreground focus:outline-none"
        >
          <option value="all">All Scan States</option>
          <option value="clean">Clean</option>
          <option value="pending">Scan Pending</option>
          <option value="infected">Infected</option>
          <option value="error">Scan Error</option>
        </select>
        <div className="flex gap-1 bg-surface-3 rounded-lg p-1">
          {[
            { key: 'grouped', label: 'Grouped', icon: Layers },
            { key: 'table', label: 'Table', icon: BookOpen },
            { key: 'audit', label: 'Audit Trail', icon: ClipboardList },
            { key: 'permissions', label: 'Permissions', icon: Users },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setActiveView(key as typeof activeView)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-all ${activeView === key ? 'bg-surface-4 text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {React.createElement(icon, { size: 12 })}
              {label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{filtered.length} docs</p>
      </div>

      {/* Loading */}
      {loading && (
        <div className="py-12 text-center">
          <RefreshCw size={24} className="animate-spin text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Loading document vault…</p>
        </div>
      )}

      {/* Grouped View */}
      {!loading && activeView === 'grouped' && (
        <div className="space-y-5">
          {allCategories.map((cat) => {
            const docs = grouped[cat];
            if (!docs || docs.length === 0) return null;
            const cfg = categoryConfig[cat];
            const CatIcon = cfg.icon;
            return (
              <div key={cat} className="glass-card rounded-xl overflow-hidden">
                <div className={`px-5 py-3.5 border-b border-border flex items-center gap-3 ${cfg.bg}`}>
                  <CatIcon size={15} className={cfg.color} />
                  <h3 className={`font-semibold text-sm ${cfg.color}`}>{cfg.label}</h3>
                  <span className={`text-xs font-mono-data px-1.5 py-0.5 rounded-full bg-black/20 ${cfg.color}`}>{docs.length}</span>
                </div>
                <div className="divide-y divide-border/50">
                  {docs.map((doc) => {
                    const statCfg = statusConfig[doc.doc_status];
                    const scanCfg = scanConfig[doc.scan_status];
                    const ndaCfg = ndaConfig[doc.nda_status];
                    const ScanIcon = scanCfg.icon;
                    const perm = permissions.find(p => p.role === CURRENT_ROLE && p.category === doc.category);
                    const canView = perm?.can_view ?? true;
                    const needsNDA = doc.requires_nda && doc.nda_status !== 'signed';
                    return (
                      <div key={doc.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-surface-3/50 transition-colors group">
                        <FileText size={14} className={`${cfg.color} flex-shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => router.push(`/document-vault/${doc.id}`)} className="font-medium text-foreground text-sm truncate hover:text-primary transition-colors text-left">{doc.name}</button>
                            {doc.requires_nda && <Lock size={11} className={ndaCfg.color} title={ndaCfg.label} />}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            {doc.campaign && <p className="text-xs text-muted-foreground truncate">{doc.campaign}</p>}
                            <p className="text-xs text-muted-foreground hidden sm:block font-mono-data">{doc.retention_label}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="flex items-center gap-1 hidden md:flex">
                            <ScanIcon size={11} className={scanCfg.color} />
                            <span className={`text-xs ${scanCfg.color}`}>{scanCfg.label}</span>
                          </div>
                          <StatusBadge variant={statCfg.variant as any} label={statCfg.label} dot />
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-mono-data text-muted-foreground">{doc.current_version}</span>
                            {doc.versions && doc.versions.length > 1 && (
                              <button onClick={() => setHistoryDoc(doc)} className="flex items-center gap-0.5 text-xs text-accent hover:text-accent/80 ml-1">
                                <History size={11} />
                                <span>{doc.versions.length}</span>
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {canView && (
                              <button
                                onClick={() => needsNDA ? setNdaDoc(doc) : undefined}
                                className="btn-ghost p-1.5"
                                title={needsNDA ? 'Sign NDA to view' : 'View'}
                              >
                                {needsNDA ? <Lock size={13} className="text-warning" /> : <Eye size={13} />}
                              </button>
                            )}
                            {perm?.can_download && !needsNDA && (
                              <button className="btn-ghost p-1.5" title="Download"><Download size={13} /></button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-12 text-center glass-card rounded-xl">
              <Archive size={28} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No documents match your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Table View */}
      {!loading && activeView === 'table' && (
        <div className="glass-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-3">
                <tr>
                  {['Document', 'Category', 'Virus Scan', 'Status', 'Version', 'Retention', 'Actions'].map((h) => (
                    <th key={h} className="table-th">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    onViewHistory={setHistoryDoc}
                    onNDAGate={setNdaDoc}
                    currentRole={CURRENT_ROLE}
                    permissions={permissions}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="py-12 text-center">
              <Archive size={28} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No documents match your search</p>
            </div>
          )}
        </div>
      )}

      {/* Audit Trail View */}
      {activeView === 'audit' && (
        <AuditTrailPanel entries={auditEntries} loading={auditLoading} />
      )}

      {/* Permissions View */}
      {activeView === 'permissions' && (
        <RolePermissionsPanel permissions={permissions} />
      )}

      {/* Modals */}
      {historyDoc && <VersionHistoryDrawer doc={historyDoc} onClose={() => setHistoryDoc(null)} />}
      {ndaDoc && <NDAGateModal doc={ndaDoc} onSign={handleNDASign} onClose={() => setNdaDoc(null)} />}
      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUpload={handleUpload} permissions={permissions} />}
    </div>
  );
}

// ─── Fallback Data ────────────────────────────────────────────────────────────

function getFallbackDocuments(): VaultDocument[] {
  return [
    { id: 'f1', doc_ref: 'VD-KYC-001', name: 'KYC Identity Verification Package', category: 'KYC', doc_status: 'active', file_size_bytes: 4404019, current_version: 'v3.0', access_level: 'Private', signed_by: 'Fatima Al-Rashidi', expiry_date: '2027-02-01', allowed_roles: ['investor','compliance','admin','auditor'], uploaded_by: 'Compliance Team', uploaded_by_role: 'compliance', requires_nda: false, nda_status: 'not_required', scan_status: 'clean', scan_completed_at: new Date().toISOString(), retention_days: 2555, retention_label: '7 years (regulatory)', retention_expires_at: new Date(Date.now() + 7 * 365 * 86400000).toISOString(), auto_delete_on_expiry: false, tags: ['identity','passport'], created_at: new Date().toISOString(), updated_at: new Date().toISOString(), versions: [{ id: 'v1', version: 'v3.0', file_size_bytes: 4404019, uploaded_by: 'Compliance Team', uploaded_by_role: 'compliance', change_note: 'Annual renewal', scan_status: 'clean', is_current: true, created_at: new Date().toISOString() }] },
    { id: 'f2', doc_ref: 'VD-NDA-001', name: 'NDA — Meridian Logistics Hub Series B', category: 'NDA', campaign: 'Meridian Logistics Hub — Series B', doc_status: 'active', file_size_bytes: 629145, current_version: 'v1.0', access_level: 'Confidential', allowed_roles: ['investor','compliance','admin'], uploaded_by: 'Platform', uploaded_by_role: 'system', requires_nda: true, nda_status: 'signed', nda_signed_at: new Date().toISOString(), nda_signed_by: 'Alexandra Chen', scan_status: 'clean', retention_days: 1825, retention_label: '5 years', retention_expires_at: new Date(Date.now() + 5 * 365 * 86400000).toISOString(), auto_delete_on_expiry: false, tags: ['nda','meridian'], created_at: new Date().toISOString(), updated_at: new Date().toISOString(), versions: [] },
    { id: 'f3', doc_ref: 'VD-DISC-001', name: 'Meridian Series B — Investor Disclosure Statement', category: 'Campaign Disclosure', campaign: 'Meridian Logistics Hub — Series B', doc_status: 'active', file_size_bytes: 2097152, current_version: 'v2.0', access_level: 'Confidential', allowed_roles: ['investor','compliance','admin','auditor'], uploaded_by: 'Issuer', uploaded_by_role: 'issuer', requires_nda: true, nda_status: 'pending', scan_status: 'clean', retention_days: 1825, retention_label: '5 years', retention_expires_at: new Date(Date.now() + 5 * 365 * 86400000).toISOString(), auto_delete_on_expiry: false, tags: ['disclosure','meridian'], created_at: new Date().toISOString(), updated_at: new Date().toISOString(), versions: [] },
    { id: 'f4', doc_ref: 'VD-COMP-001', name: 'AML Programme — Annual Review 2026', category: 'Compliance Evidence', doc_status: 'active', file_size_bytes: 1572864, current_version: 'v1.0', access_level: 'Restricted', allowed_roles: ['compliance','admin','auditor'], uploaded_by: 'Compliance Team', uploaded_by_role: 'compliance', requires_nda: false, nda_status: 'not_required', scan_status: 'clean', retention_days: 3650, retention_label: '10 years (MLRO)', retention_expires_at: new Date(Date.now() + 10 * 365 * 86400000).toISOString(), auto_delete_on_expiry: false, tags: ['aml','programme'], created_at: new Date().toISOString(), updated_at: new Date().toISOString(), versions: [] },
    { id: 'f5', doc_ref: 'VD-AGREE-001', name: 'Subscription Agreement — Meridian Series B', category: 'Investment Agreement', campaign: 'Meridian Logistics Hub — Series B', doc_status: 'active', file_size_bytes: 1887436, current_version: 'v2.0', access_level: 'Confidential', allowed_roles: ['investor','compliance','admin','auditor','trustee','finance'], uploaded_by: 'Platform', uploaded_by_role: 'system', requires_nda: false, nda_status: 'not_required', scan_status: 'clean', retention_days: 3650, retention_label: '10 years (legal)', retention_expires_at: new Date(Date.now() + 10 * 365 * 86400000).toISOString(), auto_delete_on_expiry: false, tags: ['subscription','meridian'], created_at: new Date().toISOString(), updated_at: new Date().toISOString(), versions: [{ id: 'v2', version: 'v2.0', file_size_bytes: 1887436, uploaded_by: 'Platform', uploaded_by_role: 'system', change_note: 'Final executed agreement', scan_status: 'clean', is_current: true, created_at: new Date().toISOString() }, { id: 'v1', version: 'v1.0', file_size_bytes: 1782579, uploaded_by: 'Platform', uploaded_by_role: 'system', change_note: 'Draft for review', scan_status: 'clean', is_current: false, created_at: new Date(Date.now() - 86400000 * 12).toISOString() }] },
  ];
}

function getFallbackAuditEntries(): AuditEntry[] {
  return [
    { id: 'a1', doc_ref: 'VD-KYC-001', doc_name: 'KYC Identity Verification Package', action: 'upload', actor_name: 'Compliance Team', actor_role: 'compliance', actor_ip: '10.0.1.15', details: { version: 'v3.0' }, outcome: 'success', created_at: new Date(Date.now() - 86400000 * 2).toISOString() },
    { id: 'a2', doc_ref: 'VD-KYC-001', doc_name: 'KYC Identity Verification Package', action: 'scan_completed', actor_name: 'ClamAV Engine', actor_role: 'system', actor_ip: '127.0.0.1', details: { result: 'clean' }, outcome: 'success', created_at: new Date(Date.now() - 86400000 * 2).toISOString() },
    { id: 'a3', doc_ref: 'VD-NDA-001', doc_name: 'NDA — Meridian Logistics Hub Series B', action: 'nda_signed', actor_name: 'Alexandra Chen', actor_role: 'investor', actor_ip: '203.0.113.42', details: { campaign: 'Meridian Logistics Hub — Series B' }, outcome: 'success', created_at: new Date(Date.now() - 86400000 * 90).toISOString() },
    { id: 'a4', doc_ref: 'VD-DISC-001', doc_name: 'Meridian Series B — Investor Disclosure Statement', action: 'access_denied', actor_name: 'Unknown Investor', actor_role: 'investor', actor_ip: '198.51.100.7', details: { reason: 'NDA not signed' }, outcome: 'denied', created_at: new Date(Date.now() - 86400000 * 5).toISOString() },
    { id: 'a5', doc_ref: 'VD-COMP-001', doc_name: 'AML Programme — Annual Review 2026', action: 'view', actor_name: 'Fatima Al-Rashidi', actor_role: 'compliance', actor_ip: '10.0.1.20', details: { version: 'v1.0' }, outcome: 'success', created_at: new Date(Date.now() - 86400000 * 30).toISOString() },
    { id: 'a6', doc_ref: 'VD-AGREE-001', doc_name: 'Subscription Agreement — Meridian Series B', action: 'version_added', actor_name: 'Platform', actor_role: 'system', actor_ip: '127.0.0.1', details: { old_version: 'v1.0', new_version: 'v2.0' }, outcome: 'success', created_at: new Date(Date.now() - 86400000 * 30).toISOString() },
    { id: 'a7', doc_ref: 'VD-KYC-002', doc_name: 'AML Screening Report — 2026', action: 'retention_applied', actor_name: 'System', actor_role: 'system', actor_ip: '127.0.0.1', details: { retention_days: 2555, label: '7 years (regulatory)' }, outcome: 'success', created_at: new Date(Date.now() - 86400000 * 60).toISOString() },
  ];
}

function getFallbackPermissions(): RolePermission[] {
  const roles = ['investor', 'compliance', 'admin', 'auditor', 'trustee', 'issuer'];
  const perms: RolePermission[] = [];
  roles.forEach(role => {
    allCategories.forEach(category => {
      perms.push({
        role, category,
        can_upload: role === 'admin' || (role === 'compliance' && ['KYC','NDA','Campaign Disclosure','Compliance Evidence','AML Report','FATCA/CRS'].includes(category)),
        can_view: role !== 'issuer' || ['NDA','Investment Agreement','Settlement','Distribution','Campaign Disclosure'].includes(category),
        can_download: ['admin','compliance','auditor'].includes(role),
        can_delete: role === 'admin',
        requires_nda_to_view: category === 'Campaign Disclosure' && role === 'investor',
        max_retention_days: category === 'Investment Agreement' || category === 'Compliance Evidence' || category === 'AML Report' ? 3650 : category === 'NDA' || category === 'Campaign Disclosure' ? 1825 : 2555,
      });
    });
  });
  return perms;
}
